<form [formGroup]="cameraForm" (ngSubmit)="onSubmit()" class="create-camera-form space-y-5">
    @if (isProcessing) {
    <mat-progress-bar mode="indeterminate" class="rounded-full"></mat-progress-bar>
    }

    <!-- Edit Mode Camera Identity Hero Banner -->
    @if (mode === 'edit' && camera) {
    <div class="bg-gradient-to-r from-indigo-50/90 via-slate-50 to-indigo-50/50 border border-indigo-100 rounded-2xl p-4 sm:p-5 shadow-2xs">
        <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex items-center gap-3">
                <div class="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-sm">
                    <mat-icon class="!w-6 !h-6 !text-[24px]">videocam</mat-icon>
                </div>
                <div>
                    <div class="flex items-center gap-2">
                        <h2 class="text-base font-bold text-slate-900">{{ camera.deviceName || 'ANPR Camera' }}</h2>
                        <span class="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-indigo-100 text-indigo-700">
                            {{ camera.config?.type || 'Camera' }}
                        </span>
                        @if (camera.config?.logOnly) {
                        <span class="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-100 text-amber-800">
                            Log Only
                        </span>
                        }
                    </div>
                    <p class="text-xs text-slate-500 mt-0.5">{{ camera.config?.model || 'No model specified' }}</p>
                </div>
            </div>

            <!-- Camera ID Copy Hero Box -->
            <div class="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-indigo-200/80 shadow-2xs">
                <div class="flex flex-col">
                    <span class="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Camera ID</span>
                    <span class="font-mono text-xs font-bold text-slate-800 select-all">{{ camera.id }}</span>
                </div>
                <button type="button"
                        class="p-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors focus:outline-none cursor-pointer"
                        (click)="copyText(camera.id, 'Camera ID')"
                        [title]="'Copy Camera ID: ' + camera.id">
                    <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">{{ copiedField() === 'Camera ID' ? 'check' : 'content_copy' }}</mat-icon>
                </button>
            </div>
        </div>

        @if (camera.deviceHardwareID) {
        <div class="mt-3 pt-2.5 border-t border-indigo-100/60 flex items-center gap-2 text-xs text-slate-500">
            <span class="font-semibold text-slate-400">Hardware ID:</span>
            <code class="font-mono text-[11px] text-slate-700 bg-white/80 px-2 py-0.5 rounded border border-slate-200">{{ camera.deviceHardwareID }}</code>
            <button type="button" class="text-slate-400 hover:text-indigo-600 p-0.5" (click)="copyText(camera.deviceHardwareID, 'Hardware ID')" title="Copy Hardware ID">
                <mat-icon class="!w-3.5 !h-3.5 !text-[14px]">content_copy</mat-icon>
            </button>
        </div>
        }
    </div>
    }

    <!-- Section 1: Basic Information -->
    <div class="form-section">
        <h3 class="section-title">
            <mat-icon class="!w-4 !h-4 !text-[18px] text-indigo-600">badge</mat-icon>
            Basic Information
        </h3>

        <div class="form-row">
            <mat-form-field appearance="fill" class="full-width">
                <mat-label>Camera Name</mat-label>
                <input matInput formControlName="deviceName" placeholder="e.g., Entrance Camera 1">
                <mat-icon matSuffix>videocam</mat-icon>
                @if (cameraForm.get('deviceName')?.hasError('required')) {
                <mat-error>Camera name is required</mat-error>
                }
                @if (cameraForm.get('deviceName')?.hasError('minlength')) {
                <mat-error>Camera name must be at least 2 characters</mat-error>
                }
            </mat-form-field>
        </div>

        <div class="form-row-two">
            <!-- DeviceHardwareID -->
            <mat-form-field appearance="fill" class="half-width">
                <mat-label>Device Hardware ID</mat-label>
                <input matInput formControlName="deviceHardwareID" placeholder="Leave empty to auto-generate">
                <mat-icon matSuffix class="cursor-pointer text-indigo-600 hover:text-indigo-800" (click)="generateGUID()" title="Generate new GUID">
                    auto_awesome
                </mat-icon>
                <mat-hint>Unique hardware identifier (GUID)</mat-hint>
            </mat-form-field>

            <!-- Camera Info -->
            <mat-form-field appearance="fill" class="half-width">
                <mat-label>Camera Info (Optional)</mat-label>
                <input matInput formControlName="cameraInfo" placeholder="e.g., North Gate Pole 3">
                <mat-icon matSuffix>description</mat-icon>
            </mat-form-field>
        </div>

        <div class="form-row">
            <mat-form-field appearance="fill" class="full-width">
                <mat-label>Device Description</mat-label>
                <textarea matInput formControlName="deviceDescription" placeholder="Notes or description about this camera location" rows="2"></textarea>
                <mat-icon matSuffix>note</mat-icon>
            </mat-form-field>
        </div>
    </div>

    <!-- Section 2: Network & Credentials -->
    <div class="form-section" formGroupName="config">
        <h3 class="section-title">
            <mat-icon class="!w-4 !h-4 !text-[18px] text-emerald-600">settings_ethernet</mat-icon>
            Network & Connection
        </h3>

        <div class="form-row-two">
            <mat-form-field appearance="fill" class="half-width">
                <mat-label>IP Address</mat-label>
                <input matInput formControlName="ipAddress" placeholder="192.168.1.100">
                <mat-icon matSuffix>cloud</mat-icon>
                @if (cameraForm.get('config.ipAddress')?.hasError('required')) {
                <mat-error>IP address is required</mat-error>
                }
                @if (cameraForm.get('config.ipAddress')?.hasError('pattern')) {
                <mat-error>Invalid IPv4 address format (e.g. 192.168.1.100)</mat-error>
                }
            </mat-form-field>

            <mat-form-field appearance="fill" class="half-width">
                <mat-label>Port</mat-label>
                <input matInput type="number" formControlName="port" placeholder="80">
                <mat-icon matSuffix>settings_input_component</mat-icon>
                @if (cameraForm.get('config.port')?.hasError('required')) {
                <mat-error>Port is required</mat-error>
                }
                @if (cameraForm.get('config.port')?.hasError('min') || cameraForm.get('config.port')?.hasError('max')) {
                <mat-error>Port must be between 1 and 65535</mat-error>
                }
            </mat-form-field>
        </div>

        <div class="form-row-two">
            <mat-form-field appearance="fill" class="half-width">
                <mat-label>Username</mat-label>
                <input matInput formControlName="username" placeholder="admin">
                <mat-icon matSuffix>person</mat-icon>
                @if (cameraForm.get('config.username')?.hasError('required')) {
                <mat-error>Username is required</mat-error>
                }
            </mat-form-field>

            <mat-form-field appearance="fill" class="half-width">
                <mat-label>Password</mat-label>
                <input matInput [type]="hidePassword() ? 'password' : 'text'" formControlName="password" placeholder="••••••••">
                <button type="button" mat-icon-button matSuffix (click)="hidePassword.set(!hidePassword())" [attr.aria-label]="'Toggle password visibility'">
                    <mat-icon>{{ hidePassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                @if (mode === 'edit') {
                <mat-hint>Leave blank to keep existing password.</mat-hint>
                }
                @if (cameraForm.get('config.password')?.hasError('required')) {
                <mat-error>Password is required</mat-error>
                }
            </mat-form-field>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2 border-y border-slate-200/60 my-2">
            <mat-checkbox formControlName="useHttps" (change)="onHttpsChange($event.checked)" class="checkbox-item">
                <span class="checkbox-label font-semibold text-slate-800">Use HTTPS (Secure Connection)</span>
            </mat-checkbox>

            <mat-checkbox formControlName="logOnly" class="checkbox-item">
                <span class="checkbox-label font-semibold text-slate-800">Log Only Mode</span>
                <span class="text-[11px] text-slate-400 block ml-6">Log plates without triggering parking transactions</span>
            </mat-checkbox>
        </div>

        <div class="form-row mt-3">
            <mat-form-field appearance="fill" class="full-width">
                <mat-label>Time Zone</mat-label>
                <mat-select formControlName="timeZoneId">
                    <mat-optgroup label="Available Timezones">
                        @for (tz of timeZoneOptions; track tz.id) {
                        <mat-option [value]="tz.id">
                            {{ tz.label }}
                        </mat-option>
                        }
                    </mat-optgroup>
                </mat-select>
                <mat-icon matSuffix>schedule</mat-icon>
                <mat-hint>Used to convert camera detection timestamps to UTC</mat-hint>
            </mat-form-field>
        </div>
    </div>

    <!-- Section 3: ANPR Model & Video Settings -->
    <div class="form-section" formGroupName="config">
        <h3 class="section-title">
            <mat-icon class="!w-4 !h-4 !text-[18px] text-purple-600">center_focus_strong</mat-icon>
            ANPR Engine & Model
        </h3>

        <div class="form-row-two">
            <mat-form-field appearance="fill" class="half-width">
                <mat-label>Camera Type</mat-label>
                <mat-select formControlName="type">
                    @for (cameraType of cameraTypeOptions; track cameraType) {
                    <mat-option [value]="cameraType">
                        {{ cameraType }}
                    </mat-option>
                    }
                </mat-select>
                <mat-icon matSuffix>category</mat-icon>
                @if (cameraForm.get('config.type')?.hasError('required')) {
                <mat-error>Camera type is required</mat-error>
                }
            </mat-form-field>

            <mat-form-field appearance="fill" class="half-width">
                <mat-label>Camera Model</mat-label>
                <input matInput formControlName="model" placeholder="e.g., DS-2CD2143G0-I or ITC237">
                <mat-icon matSuffix>info</mat-icon>
                @if (cameraForm.get('config.model')?.hasError('required')) {
                <mat-error>Camera model is required</mat-error>
                }
            </mat-form-field>
        </div>

        <div class="form-row">
            <mat-form-field appearance="fill" class="full-width">
                <mat-label>Confidence Reference Scale</mat-label>
                <mat-select formControlName="confidenceReference">
                    @for (ref of confidenceReferenceOptions; track ref) {
                    <mat-option [value]="ref">
                        {{ ConfidenceReferenceShowValue(ref!) }}
                    </mat-option>
                    }
                </mat-select>
                <mat-icon matSuffix>tune</mat-icon>
                <mat-hint>Scale used by camera firmware when returning ANPR confidence values</mat-hint>
            </mat-form-field>
        </div>
    </div>

    <!-- Section 4: Direction Mappings -->
    <div class="form-section" formGroupName="config">
        <h3 class="section-title">
            <mat-icon class="!w-4 !h-4 !text-[18px] text-amber-600">alt_route</mat-icon>
            Lane Direction Mappings
        </h3>
        <p class="text-xs text-slate-500 -mt-2 mb-3">Map camera detection movement vectors to parking entry (In), exit (Out), or transit (PassBy).</p>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <mat-form-field appearance="fill" class="full-width">
                <mat-label>⬅️ Left Direction</mat-label>
                <mat-select formControlName="leftDirectionMapping">
                    @for (dir of pointDirectionOptions; track dir) {
                    <mat-option [value]="dir">{{ getDirectionLabel(dir) }}</mat-option>
                    }
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="fill" class="full-width">
                <mat-label>➡️ Right Direction</mat-label>
                <mat-select formControlName="rightDirectionMapping">
                    @for (dir of pointDirectionOptions; track dir) {
                    <mat-option [value]="dir">{{ getDirectionLabel(dir) }}</mat-option>
                    }
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="fill" class="full-width">
                <mat-label>⬆️ Up Direction</mat-label>
                <mat-select formControlName="upDirectionMapping">
                    @for (dir of pointDirectionOptions; track dir) {
                    <mat-option [value]="dir">{{ getDirectionLabel(dir) }}</mat-option>
                    }
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="fill" class="full-width">
                <mat-label>⬇️ Down Direction</mat-label>
                <mat-select formControlName="downDirectionMapping">
                    @for (dir of pointDirectionOptions; track dir) {
                    <mat-option [value]="dir">{{ getDirectionLabel(dir) }}</mat-option>
                    }
                </mat-select>
            </mat-form-field>
        </div>
    </div>

    <!-- Section 5: Firmware & Physical Location (Collapsible) -->
    <div class="form-section">
        <button type="button" mat-button (click)="toggleAdvanced()" class="toggle-advanced">
            <mat-icon>{{ showAdvanced() ? 'expand_less' : 'expand_more' }}</mat-icon>
            <span>{{ showAdvanced() ? 'Hide' : 'Show' }} Advanced Firmware & Location Settings</span>
        </button>

        @if (showAdvanced()) {
        <div class="advanced-settings space-y-4">
            <!-- Firmware Versions -->
            <div>
                <h4 class="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Firmware & Software Versions</h4>
                <div class="form-row-two">
                    <mat-form-field appearance="fill" class="half-width">
                        <mat-label>Algorithm Version</mat-label>
                        <input matInput formControlName="algorithmVersion" placeholder="e.g., 1.0.0">
                        <mat-icon matSuffix>code</mat-icon>
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="half-width">
                        <mat-label>System Version</mat-label>
                        <input matInput formControlName="systemVersion" placeholder="e.g., 2.1.0">
                        <mat-icon matSuffix>update</mat-icon>
                    </mat-form-field>
                </div>

                <div class="form-row">
                    <mat-form-field appearance="fill" class="full-width">
                        <mat-label>Build Date</mat-label>
                        <input matInput type="date" formControlName="buildDate">
                        <mat-icon matSuffix>calendar_today</mat-icon>
                    </mat-form-field>
                </div>
            </div>

            <!-- Device Address -->
            <div formGroupName="deviceAddress">
                <h4 class="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Physical Location / Address</h4>
                <div class="form-row-two">
                    <mat-form-field appearance="fill" class="half-width">
                        <mat-label>Address Line 1</mat-label>
                        <input matInput formControlName="firstAddressLine" placeholder="e.g., 123 Main St">
                        <mat-icon matSuffix>home</mat-icon>
                    </mat-form-field>
                    <mat-form-field appearance="fill" class="half-width">
                        <mat-label>Address Line 2</mat-label>
                        <input matInput formControlName="secondAddressLine" placeholder="e.g., Gate 3">
                        <mat-icon matSuffix>apartment</mat-icon>
                    </mat-form-field>
                </div>
                <div class="form-row-two">
                    <mat-form-field appearance="fill" class="half-width">
                        <mat-label>City/Locality</mat-label>
                        <input matInput formControlName="locality" placeholder="e.g., New York">
                        <mat-icon matSuffix>location_city</mat-icon>
                    </mat-form-field>
                    <mat-form-field appearance="fill" class="half-width">
                        <mat-label>Post Code</mat-label>
                        <input matInput formControlName="postCode" placeholder="e.g., 10001">
                        <mat-icon matSuffix>local_post_office</mat-icon>
                    </mat-form-field>
                </div>
                <div class="form-row-two">
                    <mat-form-field appearance="fill" class="half-width">
                        <mat-label>Region</mat-label>
                        <input matInput formControlName="region" placeholder="e.g., NY">
                        <mat-icon matSuffix>public</mat-icon>
                    </mat-form-field>
                    <mat-form-field appearance="fill" class="half-width">
                        <mat-label>Country Code</mat-label>
                        <input matInput formControlName="countryCode" placeholder="e.g., US">
                        <mat-icon matSuffix>flag</mat-icon>
                    </mat-form-field>
                </div>
                <div class="form-row-two">
                    <mat-form-field appearance="fill" class="half-width">
                        <mat-label>Latitude</mat-label>
                        <input matInput type="number" formControlName="latitude" placeholder="e.g., 40.7128">
                        <mat-icon matSuffix>explore</mat-icon>
                    </mat-form-field>
                    <mat-form-field appearance="fill" class="half-width">
                        <mat-label>Longitude</mat-label>
                        <input matInput type="number" formControlName="longitude" placeholder="e.g., -74.0060">
                        <mat-icon matSuffix>explore_off</mat-icon>
                    </mat-form-field>
                </div>
            </div>
        </div>
        }
    </div>

    <!-- Form Actions & Validation Feedback -->
    <div class="form-actions">
        <!-- Validation Tip if Invalid -->
        @if (cameraForm.invalid && cameraForm.touched) {
        <div class="validation-tip w-full mb-3">
            <div class="flex items-center gap-1.5 font-bold text-rose-700">
                <mat-icon class="!w-4 !h-4 !text-[16px]">error_outline</mat-icon>
                <span>Please complete the required fields:</span>
            </div>
            <ul class="list-disc list-inside mt-1 space-y-0.5 text-xs text-rose-600">
                @if (cameraForm.get('deviceName')?.invalid) {
                <li>Camera Name is required (at least 2 characters)</li>
                }
                @if (cameraForm.get('config.ipAddress')?.hasError('required')) {
                <li>IP Address is required</li>
                }
                @if (cameraForm.get('config.ipAddress')?.hasError('pattern')) {
                <li>IP Address format is invalid</li>
                }
                @if (cameraForm.get('config.port')?.invalid) {
                <li>Port is required (1 - 65535)</li>
                }
                @if (cameraForm.get('config.username')?.invalid) {
                <li>Username is required</li>
                }
                @if (cameraForm.get('config.password')?.invalid) {
                <li>Password is required</li>
                }
                @if (cameraForm.get('config.type')?.invalid) {
                <li>Camera Type is required</li>
                }
                @if (cameraForm.get('config.model')?.invalid) {
                <li>Camera Model is required</li>
                }
            </ul>
        </div>
        }

        <div class="flex items-center justify-end gap-3 w-full">
            <button mat-stroked-button (click)="onCancel()" type="button" [disabled]="isProcessing">
                <mat-icon>close</mat-icon>
                Cancel
            </button>
            <button mat-flat-button color="primary" type="submit" [disabled]="isProcessing || !cameraForm.valid">
                <mat-icon>{{ isProcessing ? 'hourglass_empty' : 'check' }}</mat-icon>
                {{ isProcessing ? (mode === 'edit' ? 'Saving...' : 'Creating...') : (mode === 'edit' ? 'Save Changes' : 'Create Camera') }}
            </button>
        </div>
    </div>
</form>